import React, {useCallback, useEffect, useRef, useState} from 'react'
import {connect} from "react-redux";
import {useRoute} from "react-router5";

import Popup from "../../../components/Popup";
import IntegrationWizard, {IntegrationWizardImportState, IntegrationWizardStep} from "../../../components/IntegrationWizard";
import notifyUser, {notifyAboutError} from "../../../methods/notifyUser";
import {httpClient} from "../../../funcs";
import {importRealtyObjects} from "../../../methods/importRealtyObjects";

import "pages/Styles/IntegrationsWizard.sass"

type Props = {
    close: Function,
    $store: any,
    $commitToStore: Function,
}

type WizardProgress = {
    current_step_index: number
    completed_step_ids: string[]
    data?: {
        selected_template?: any
    }
}

const FLOW_ID = "realty_v2_setup"

const REALTY_STEPS: IntegrationWizardStep[] = [
    {
        id: "choose_template",
        type: "template_select",
        title: "Шаг 1. Выберите шаблон договора",
        description: "Перед настройкой интеграции нужен шаблон договора.",
        list: [
            "Можно загрузить свой шаблон или выбрать готовый из каталога.",
            "После выбора мы запомним шаблон для следующих шагов."
        ]
    },
    {
        id: "copy_api_key",
        type: "api_key_copy",
        title: "Шаг 2. Скопируйте API ключ",
        description: "Ваш API ключ для RealtyCalendar:"
    },
    {
        id: "open_realty",
        type: "external_link",
        title: "Шаг 3. Настройте интеграцию в RealtyCalendar",
        description: "Откройте настройки интеграции в RealtyCalendar и заполните данные.",
        list: [
            "Вставьте Токен ОкиДоки (API ключ).",
            "Выберите шаблон: {selectedTemplate}."
        ],
        linkUrl: "https://realtycalendar.ru/settings/#/integration",
        ctaLabel: "Перейти в RealtyCalendar"
    },
    {
        id: "import_objects",
        type: "import_objects",
        title: "Шаг 4. Импортируйте объекты",
        description: "Импорт добавит список объектов и обязательные поля интеграции в выбранный шаблон.",
        list: [
            "Будет создано поле «Описание и адрес квартиры» с объектами.",
            "Для каждого объекта добавятся «Адрес» и «Перечень имущества» (пустое).",
            "Добавятся поля интеграции: дата заселения/выселения, цена, стоимость, депозит, количество суток."
        ],
        hint: "Важно: существующее поле «Описание и адрес квартиры» будет заменено новым.",
        ctaLabel: "Импортировать объекты"
    },
    {
        id: "finish",
        type: "info",
        title: "Шаг 5. Интеграция завершена",
        description: "Теперь в RealtyCalendar в карточке бронирования, в разделе «Онлайн-договор», можно создавать договоры и отслеживать статус подписания.",
        list: [
            "Проверьте, что в шаблоне договора есть метки всех полей, которые были загружены из RealtyCalendar.",
            "Если были ранее созданные аналогичные поля, удалите их.",
            "При создании договора в загруженные поля будут подставляться данные из RealtyCalendar."
        ]
    }
]

let RealtyIntegrationSetupPopup: any = function RealtyIntegrationSetupPopup(props: Props) {
    const $router = useRoute()
    const realtyIntegration = props.$store.userSession?.realty_integration || {}
    const savedProgress: WizardProgress | undefined = realtyIntegration?.setup_progress?.[FLOW_ID]

    const [currentStepIndex, setCurrentStepIndex] = useState<number>(
        Math.min(savedProgress?.current_step_index ?? 0, REALTY_STEPS.length - 1)
    )
    const [completedStepIds, setCompletedStepIds] = useState<Set<string>>(
        new Set(savedProgress?.completed_step_ids ?? [])
    )
    const [selectedTemplate, setSelectedTemplate] = useState<any>(
        savedProgress?.data?.selected_template ?? null
    )

    const [importState, setImportState] = useState<IntegrationWizardImportState>({
        loading: false,
        success: false,
        importedCount: 0,
        apiKeyError: false
    })

    const lastSavedRef = useRef<string>("")

    const isStepComplete = useCallback(
        (stepId: string) => completedStepIds.has(stepId),
        [completedStepIds]
    )

    const completeStep = useCallback((stepId: string) => {
        setCompletedStepIds((prev) => {
            if (prev.has(stepId)) {
                return prev
            }
            const next = new Set(prev)
            next.add(stepId)
            return next
        })
    }, [])

    useEffect(() => {
        if (selectedTemplate?.id) {
            completeStep("choose_template")
        }
    }, [selectedTemplate, completeStep])

    useEffect(() => {
        if (importState.success) {
            completeStep("import_objects")
        }
    }, [importState.success, completeStep])

    const saveProgress = useCallback(async (progress: WizardProgress) => {
        try {
            const existingSetup = realtyIntegration?.setup_progress || {}
            await httpClient.patch("/user/self", {
                realty_integration: {
                    ...realtyIntegration,
                    setup_progress: {
                        ...existingSetup,
                        [FLOW_ID]: progress
                    }
                }
            })

            await httpClient.get('/user/self').then(({ data }) => {
                props.$commitToStore({
                    userSession: data
                })
            })
        } catch (error) {
            notifyAboutError(props, error)
        }
    }, [props, realtyIntegration])

    useEffect(() => {
        const progress: WizardProgress = {
            current_step_index: currentStepIndex,
            completed_step_ids: Array.from(completedStepIds),
            data: {
                selected_template: selectedTemplate
            }
        }
        const serialized = JSON.stringify(progress)
        if (serialized === lastSavedRef.current) return
        lastSavedRef.current = serialized
        void saveProgress(progress)
    }, [currentStepIndex, completedStepIds, selectedTemplate, saveProgress])

    function onCopyApiKey() {
        notifyUser(props, "API ключ скопирован в буфер обмена")
        completeStep("copy_api_key")
    }

    function navigateToTemplates() {
        $router.router.navigate("new-contract", {act: "list"}, {reload: false})
        props.close()
    }

    async function importObjects() {
        if (!props.$store.userSession?.realty_api_key) {
            notifyUser(props, "Ошибка", "API ключ RealtyCalendar не найден")
            setImportState((prev) => ({...prev, apiKeyError: true}))
            return
        }

        if (!selectedTemplate?.id) {
            notifyUser(props, "Ошибка", "Выберите шаблон для импорта")
            return
        }

        setImportState((prev) => ({...prev, loading: true, apiKeyError: false}))
        try {
            const result = await importRealtyObjects(
                selectedTemplate.id,
                props.$store.userSession.realty_api_key
            )

            if (result.objects_count === 0) {
                notifyUser(props, "Информация", "Объекты не найдены в RealtyCalendar")
                setImportState((prev) => ({...prev, loading: false}))
                return
            }

            await httpClient.get('/user/self').then(({ data }) => {
                props.$commitToStore({ userSession: data })
            })

            setImportState({
                loading: false,
                success: true,
                importedCount: result.objects_count,
                apiKeyError: false
            })
        } catch (error: any) {
            console.error('Error importing realty objects:', error)
            if (error.response?.status === 401) {
                setImportState((prev) => ({...prev, loading: false, apiKeyError: true}))
            } else if (error.response?.data?.includes?.('Неверный формат API ключа')) {
                notifyUser(props, "Ошибка", "Неверный формат API ключа RealtyCalendar")
                setImportState((prev) => ({...prev, loading: false}))
            } else {
                notifyAboutError(props, error)
                setImportState((prev) => ({...prev, loading: false}))
            }
        }
    }

    return (
        <Popup
            maxWidth="650px"
            onClose={() => props.close()}
        >
            <div className="realty-integration-setup ui-kit-v2">
                <div className="commonLabel ui-kit-v2 realty-integration-setup__title">
                    Интеграция с RealtyCalendar 2.0
                </div>
                <div className="commonSubtitle ui-kit-v2 realty-integration-setup__subtitle">
                    Настройка интеграции в формате пошаговой инструкции.
                </div>

                <IntegrationWizard
                    steps={REALTY_STEPS}
                    currentStepIndex={currentStepIndex}
                    isStepComplete={isStepComplete}
                    onCompleteStep={completeStep}
                    onChangeStep={setCurrentStepIndex}
                    onFinish={() => props.close()}
                    context={{
                        apiKey: props.$store.userSession?.realty_api_key || "",
                        onCopyApiKey,
                        selectedTemplate,
                        onSelectTemplate: setSelectedTemplate,
                        onNavigateTemplates: navigateToTemplates,
                        importState,
                        onImportObjects: importObjects
                    }}
                />
            </div>
        </Popup>
    )
}

RealtyIntegrationSetupPopup = connect(
    (store) => ({ $store: store }),
    (dispatch) => ({
        $commitToStore: (data: any) => dispatch({ ...data, type: 'S' })
    })
)(RealtyIntegrationSetupPopup)

export default RealtyIntegrationSetupPopup
